import { SITE_URL, siteUrl } from '@/lib/site-config';
import { getAuthorProfile } from '@/lib/author';

interface SchemaData {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  breadcrumbs?: Array<{ name: string; url: string }>;
  /** true only when this page also emits its BreadcrumbList, so WebPage.breadcrumb never dangles */
  linkBreadcrumb?: boolean;
  faqs?: Array<{ question: string; answer: string; schema?: boolean }>;
  itemCount?: number;
  wordCount?: number;
  readingTime?: string;
}

interface SchemaMarkupProps {
  type: 'WebPage' | 'Article' | 'FAQPage' | 'BreadcrumbList' | 'SoftwareApplication' | 'Service' | 'Review' | 'AggregateRating' | 'HowTo' | 'CollectionPage' | 'Offer' | 'Product' | 'Blog' | 'AboutPage' | 'ContactPage' | 'Person';
  data: SchemaData;
}

export function SchemaMarkup({ type, data }: SchemaMarkupProps) {
  let schema: any = {};

  switch (type) {
    case 'WebPage':
      schema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${siteUrl(data.url || '')}#webpage`,
        "name": data.title,
        "description": data.description,
        "url": siteUrl(data.url || ''),
        "inLanguage": "en-US",
        "isPartOf": { "@id": `${SITE_URL}/#website` },
        "publisher": { "@id": `${SITE_URL}/#organization` },
        ...(data.publishedTime ? { "datePublished": data.publishedTime } : {}),
        ...(data.modifiedTime ? { "dateModified": data.modifiedTime } : {}),
        ...(data.image ? { "primaryImageOfPage": { "@type": "ImageObject", "url": siteUrl(data.image) } } : {}),
        ...(data.linkBreadcrumb && data.breadcrumbs?.length ? { "breadcrumb": { "@id": `${siteUrl(data.url || '')}#breadcrumb` } } : {})
      };
      break;

    case 'Article':
      schema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": data.title,
        "description": data.description,
        "url": data.url,
        "datePublished": data.publishedTime,
        "dateModified": data.modifiedTime || data.publishedTime,
        // Author as a linked entity, not a bare string: for known authors the
        // node carries a stable @id (the on-site author page), a url, and the
        // sameAs LinkedIn profile — so every Article on the site points at ONE
        // Person entity instead of N disconnected name-only nodes.
        "author": (() => {
          const profile = getAuthorProfile(data.author);
          if (profile) {
            return {
              "@type": "Person",
              "@id": `${SITE_URL}${profile.pagePath}#person`,
              "name": profile.name,
              "url": `${SITE_URL}${profile.pagePath}`,
              "sameAs": [profile.linkedin]
            };
          }
          return {
            "@type": "Person",
            "name": data.author || "LeadPops"
          };
        })(),
        "publisher": {
          "@type": "Organization",
          "name": "LeadPops",
          "logo": {
            "@type": "ImageObject",
            "url": `${SITE_URL}/logo.png`
          }
        },
        "image": data.image || `${SITE_URL}/default-image.jpg`
      };
      break;

    case 'FAQPage':
      if (data.faqs && data.faqs.length > 0) {
        schema = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data.faqs
            .filter(faq => faq.schema !== false)
            .map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
        };
      }
      break;

    case 'BreadcrumbList':
      if (data.breadcrumbs && data.breadcrumbs.length > 0) {
        schema = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "@id": `${siteUrl(data.url || '')}#breadcrumb`,
          // Google reads the trail from the root: start at Home unless the page already does
          "itemListElement": (data.breadcrumbs[0].url === '/' ? data.breadcrumbs : [{ name: 'Home', url: '/' }, ...data.breadcrumbs]).map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": siteUrl(item.url)
          }))
        };
      }
      break;

    case 'SoftwareApplication':
      schema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#software`,
        "name": "LeadPops Mortgage Marketing Platform",
        "description": "Mortgage marketing software that helps loan officers generate 3X more qualified leads through AI technology and compliant automation.",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web-based",
        "offers": {
          "@type": "Offer",
          "price": "Custom pricing",
          "priceCurrency": "USD"
        },
        "provider": {
          "@id": `${SITE_URL}/#organization`
        }
      };
      break;

    case 'Service':
      // Emits nothing (owner ruling 2026-10-03: LeadPops is a product with a managed-services layer, not a service
      // company). The managed-services Service node lives only where it is true: serviceSchemas.mortgageMarketing and the
      // homepage's homepageSchemas.service. Pages that list Service in their frontmatter get their WebPage node only.
      break;

    case 'CollectionPage':
      schema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${siteUrl(data.url || '')}#collectionpage`,
        "name": data.title || "Collection Page",
        "description": data.description || "A collection of related content",
        "url": `${siteUrl(data.url || '')}`,
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`
        },
        "publisher": {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`
        },
        // An @id reference to the BreadcrumbList emitted as its own node. It must NOT carry an @type: Google reads that as a
        // second, empty BreadcrumbList ("Missing field itemListElement", seen in Search Console 2026-10-05).
        "breadcrumb": { "@id": `${siteUrl(data.url || '')}#breadcrumb` },
        "mainEntity": {
          "@type": "ItemList",
          "name": data.title || "Collection Items",
          "numberOfItems": data.itemCount || 10,
          "itemListOrder": "https://schema.org/ItemListOrderDescending"
        }
      };
      break;

    case 'Offer':
      schema = {
        "@context": "https://schema.org",
        "@type": "Offer",
        "@id": `${siteUrl(data.url || '')}#offer`,
        "name": data.title || "Mortgage Lead Offer",
        "description": data.description || "High-quality mortgage leads for loan officers",
        "url": `${siteUrl(data.url || '')}`,
        "seller": {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`
        },
        "itemOffered": {
          "@type": "Service",
          "name": data.title || "Mortgage Leads",
          "description": data.description || "Verified mortgage leads delivered in real-time"
        },
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "USD",
          "price": "Varies",
          "unitText": "per lead"
        },
        "eligibleRegion": {
          "@type": "Country",
          "name": "United States"
        },
        "availability": "https://schema.org/InStock",
        "validFrom": data.publishedTime || "2025-01-01"
      };
      break;

    case 'Product':
      schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${siteUrl(data.url || '')}#product`,
        "name": data.title || "Mortgage Marketing Product",
        "description": data.description || "Tools and resources for mortgage professionals",
        "url": `${siteUrl(data.url || '')}`,
        "brand": {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`
        },
        "offers": {
          "@type": "Offer",
          "priceCurrency": "USD",
          "price": "0",
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "@id": `${SITE_URL}/#organization`
          }
        },
        "isAccessibleForFree": true,
        "category": "Mortgage Marketing Tools"
      };
      break;

    case 'Blog':
      schema = {
        "@context": "https://schema.org",
        "@type": "Blog",
        "@id": `${siteUrl(data.url || '')}#blog`,
        "name": data.title || "The LeadPops Blog",
        "description": data.description || "Expert insights and strategies for mortgage professionals",
        "url": `${siteUrl(data.url || '')}`,
        "publisher": {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`
        },
        "blogPost": {
          "@type": "BlogPosting",
          "description": "Latest mortgage marketing articles and insights"
        },
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`
        },
        "inLanguage": "en-US"
      };
      break;

    case 'AboutPage':
      schema = {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "@id": `${siteUrl(data.url || '')}#aboutpage`,
        "name": data.title || "About LeadPops",
        "description": data.description || "Learn about LeadPops and our mission",
        "url": `${siteUrl(data.url || '')}`,
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`
        },
        "about": {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": data.image || `${SITE_URL}/images/about-leadpops.jpg`
        },
        "inLanguage": "en-US"
      };
      break;

    case 'ContactPage':
      schema = {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "@id": `${siteUrl(data.url || '')}#contactpage`,
        "name": data.title || "Contact LeadPops",
        "description": data.description || "Get in touch with our mortgage marketing experts",
        "url": `${siteUrl(data.url || '')}`,
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`
        },
        "publisher": {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`
        },
        "potentialAction": {
          "@type": "CommunicateAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${siteUrl(data.url || '/contact-leadpops')}`,
            "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
          }
        },
        "inLanguage": "en-US"
      };
      break;

    case 'Review':
    case 'AggregateRating':
      // Emit nothing, by design. The old nodes were a placeholder review ("Verified Customer", five stars, the page
      // description as the text) and a 4.9/750 rating copied from Google reviews, both attached by @id to a node that
      // is not on the page. Google reported "Invalid object type for field itemReviewed" and self-serving ratings are
      // against its rules. Ratings return only from first-party reviews collected and shown on leadpops.com.
      break;

    case 'Person':
      // Emit the full Person node on the author page (its canonical home) and
      // on /about-leadpops, which lists Andrew in its schema frontmatter.
      // Both carry the SAME @id — the author page — so there is exactly one
      // Andrew Pawlak entity, anchored where every byline links.
      if (data.url && (data.url.includes('author/andrew-pawlak') || data.url.includes('about-leadpops'))) {
        schema = {
          "@context": "https://schema.org",
          "@type": "Person",
          "@id": `${SITE_URL}/author/andrew-pawlak#person`,
          "name": "Andrew Pawlak",
          "givenName": "Andrew",
          "familyName": "Pawlak",
          "jobTitle": "Co-Founder and Chief Executive Officer",
          "worksFor": {
            "@type": "Organization",
            "@id": `${SITE_URL}/#organization`,
            "name": "rebel iQ Inc.",
            "alternateName": "LeadPops"
          },
          "url": `${SITE_URL}/author/andrew-pawlak`,
          "image": `${SITE_URL}/Andrew-Pawlak-LeadPops-rebeliQ.jpeg`,
          "sameAs": [
            "https://www.linkedin.com/in/andrewpawlak"
          ],
          "knowsAbout": [
            "Mortgage lead generation",
            "Loan officer marketing",
            "Conversion rate optimization for mortgage professionals",
            "Realtor partnership marketing",
            "RESPA-compliant mortgage marketing automation",
            "Mortgage funnel architecture and design"
          ],
          "alumniOf": "rebel iQ Inc.",
          "description": "Co-Founder and CEO of LeadPops by rebel iQ. Author of The Mortgage Marketing Manifesto. 15+ years building lead generation systems for the mortgage industry, including conversion infrastructure for Zillow and Bankrate. LeadPops serves 5,247+ loan officers and has generated over 3.2 million leads."
        };
      }
      break;

    case 'HowTo':
      schema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": data.title || "How to Generate More Mortgage Leads",
        "description": data.description || "Step-by-step guide to mortgage lead generation",
        "image": data.image,
        "totalTime": "PT30M",
        "estimatedCost": {
          "@type": "MonetaryAmount",
          "currency": "USD",
          "value": "0"
        },
        "tool": [
          {
            "@type": "HowToTool",
            "name": "LeadPops Platform"
          }
        ],
        "step": [
          {
            "@type": "HowToStep",
            "name": "Set up your mortgage marketing campaign",
            "text": "Configure your target market and lead preferences in the LeadPops platform."
          },
          {
            "@type": "HowToStep", 
            "name": "Activate lead generation",
            "text": "Deploy AI-powered lead generation tools to start capturing qualified mortgage prospects."
          },
          {
            "@type": "HowToStep",
            "name": "Convert leads to clients",
            "text": "Use automated follow-up sequences and realtor partnerships to convert leads into closed loans."
          }
        ]
      };
      break;
  }

  // Only render if schema has content
  if (Object.keys(schema).length === 0) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}